/**
 * useAssistantChat — chat-session state for one assistant.
 *
 * The server (agentChat) runs the whole tool loop per turn and returns every
 * persisted message row; this hook keeps the transcript, sessions list, and
 * the pending-action confirm protocol in client state:
 *
 *   send(text, attachments)  -> optimistic user bubble, then server rows
 *   approve(actionId)        -> executes the pending action server-side
 *   reject(actionId)         -> cancels it
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { callAgentStudio, callAgentChat } from '../../hooks/useAssistants';

export function useAssistantChat(agentId) {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pendingAction, setPendingAction] = useState(null);
  const [sending, setSending] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [error, setError] = useState(null);
  const seq = useRef(0);

  const loadSessions = useCallback(async () => {
    try {
      const data = await callAgentStudio('listSessions', { agentId });
      setSessions(data.sessions || []);
    } catch (_) {
      // sidebar is non-critical
    }
  }, [agentId]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const newSession = useCallback(() => {
    setSessionId(null);
    setMessages([]);
    setPendingAction(null);
    setError(null);
  }, []);

  const openSession = useCallback(async (id) => {
    setLoadingSession(true);
    setError(null);
    try {
      const data = await callAgentStudio('getSession', { sessionId: id });
      setSessionId(id);
      setMessages(data.messages || []);
      setPendingAction(data.session?.pending_action || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingSession(false);
    }
  }, []);

  const applyResponse = useCallback((data, optimisticId) => {
    setSessionId(data.sessionId);
    setMessages((prev) => {
      const kept = optimisticId ? prev.filter((m) => m.id !== optimisticId) : prev;
      return [...kept, ...(data.messages || [])];
    });
    setPendingAction(data.pendingAction || null);
  }, []);

  const send = useCallback(async (text, attachments = []) => {
    const body = String(text || '').trim();
    if ((!body && attachments.length === 0) || sending) return;
    setSending(true);
    setError(null);

    const optimisticId = `optimistic-${++seq.current}`;
    setMessages((prev) => [...prev, {
      id: optimisticId,
      role: 'user',
      content: body,
      parts: attachments.length ? { attachments: attachments.map((a) => ({ name: a.name, mimeType: a.mimeType })) } : {},
      created_at: new Date().toISOString(),
    }]);

    try {
      const data = await callAgentChat({
        agentId,
        sessionId,
        message: body,
        attachments: attachments.map((a) => ({ storagePath: a.storagePath, mimeType: a.mimeType, name: a.name })),
      });
      applyResponse(data, optimisticId);
      loadSessions();
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      setError(e.message);
    } finally {
      setSending(false);
    }
  }, [agentId, sessionId, sending, applyResponse, loadSessions]);

  const resolveAction = useCallback(async (actionId, approved) => {
    if (!sessionId || sending) return;
    setSending(true);
    setError(null);
    try {
      const data = await callAgentChat({
        agentId,
        sessionId,
        ...(approved ? { approveActionId: actionId } : { rejectActionId: actionId }),
      });
      applyResponse(data, null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  }, [agentId, sessionId, sending, applyResponse]);

  const approve = useCallback((actionId) => resolveAction(actionId, true), [resolveAction]);
  const reject = useCallback((actionId) => resolveAction(actionId, false), [resolveAction]);

  const deleteSession = useCallback(async (id) => {
    await callAgentStudio('deleteSession', { sessionId: id });
    setSessions((prev) => prev.filter((s) => s.id !== id));
    if (id === sessionId) newSession();
  }, [sessionId, newSession]);

  return {
    sessions, sessionId, messages, pendingAction, sending, loadingSession, error,
    send, approve, reject, newSession, openSession, deleteSession, loadSessions,
  };
}

export default useAssistantChat;
